import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Calendar as CalendarIcon,
  Edit,
  Loader2,
  Percent,
  Plus,
  Save,
  Trash,
  AlertTriangle,
} from 'lucide-react'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { Calendar } from '@/components/ui/calendar'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { format } from 'date-fns'
import dayjs from 'dayjs'
import toast from 'react-hot-toast'
import { Skeleton } from '@/components/ui/skeleton'
import {
  useCreateExpenseRatioHistoryMutation,
  useDeleteExpenseRatioHistoryMutation,
  useGetExpenseRatioHistoryQuery,
  useUpdateExpenseRatioHistoryMutation,
  type ExpenseRatioHistoryItem,
} from '@/redux/service/expenseRatioHistory'
import { useGetDatascopeQuery } from '@/redux/service/datascope'
import { useCheckPermission } from '@/hooks/useCheckPermission'

// API stores fractions (0..10), UI works in percentages (0..1000).
const fractionToPercent = (v: string | number | null | undefined): number => {
  const n = Number(v)
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 10000) / 100
}

const percentToFraction = (v: string | number): number => {
  const n = Number(v)
  if (!Number.isFinite(n)) return 0
  return Math.round(n * 100) / 10000
}

const ExpenseRatioSchema = Yup.object({
  expense_ratio: Yup.number()
    .typeError('Must be a number')
    .required('Expense ratio is required')
    .min(0, 'Must be between 0 and 1000')
    .max(1000, 'Must be between 0 and 1000'),
  start_date: Yup.date()
    .typeError('Start date is required')
    .required('Start date is required'),
  end_date: Yup.date()
    .nullable()
    .test(
      'end-after-start',
      'End date cannot be before start date',
      function (value) {
        const { start_date } = this.parent as { start_date?: Date | null }
        if (!value || !start_date) return true
        return !dayjs(value)
          .startOf('day')
          .isBefore(dayjs(start_date).startOf('day'))
      }
    ),
})

const formatDateDisplay = (value: string | null | undefined) => {
  if (!value) return '-'
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return '-'

  return format(d, 'dd-MM-yyyy')
}
const ExpenseRatioHistory = () => {
  const { hasPageAccess } = useCheckPermission()
  const canMutate = hasPageAccess('finance') || hasPageAccess('mini_finance')

  const { data, isLoading } = useGetExpenseRatioHistoryQuery()
  const { data: datascopeData } = useGetDatascopeQuery()
  const [createExpenseRatio, { isLoading: isCreating }] =
    useCreateExpenseRatioHistoryMutation()
  const [updateExpenseRatio, { isLoading: isUpdating }] =
    useUpdateExpenseRatioHistoryMutation()
  const [deleteExpenseRatio, { isLoading: isDeleting }] =
    useDeleteExpenseRatioHistoryMutation()

  const [isAddOpen, setIsAddOpen] = useState(false)
  const [isEditOpen, setIsEditOpen] = useState(false)
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [selectedItem, setSelectedItem] =
    useState<ExpenseRatioHistoryItem | null>(null)

  const [addStartOpen, setAddStartOpen] = useState(false)
  const [addEndOpen, setAddEndOpen] = useState(false)
  const [editStartOpen, setEditStartOpen] = useState(false)
  const [editEndOpen, setEditEndOpen] = useState(false)

  const rows = data?.data ?? []
  const today = dayjs().startOf('day')
  const hasActiveRatio = rows.some(item => {
    if (!item.start_date) return false
    const start = dayjs(item.start_date).startOf('day')
    const end = item.end_date ? dayjs(item.end_date).startOf('day') : null
    return (
      (start.isBefore(today) || start.isSame(today)) &&
      (!end || today.isBefore(end) || today.isSame(end))
    )
  })

  const isExpired = rows.length > 0 && !hasActiveRatio
  const rawRatio = datascopeData?.data?.[0]?.expense_ratio
  const numRatio = Number(rawRatio)
  const defaultRatio = Number.isFinite(numRatio)
    ? (numRatio * 100).toFixed(2)
    : '0.00'

  const handleConfirmDelete = async () => {
    if (!selectedItem) return
    try {
      await deleteExpenseRatio({ id: selectedItem.id }).unwrap()
      toast.success('Expense ratio deleted')
      setIsDeleteOpen(false)
      setSelectedItem(null)
    } catch (err) {
      const error = err as { data?: { message?: string } }
      toast.error(error?.data?.message ?? 'Delete failed')
    }
  }

  return (
    <div className='h-full flex flex-col rounded-2xl border border-border overflow-hidden shadow-sm'>
      <div className='shrink-0 flex items-center justify-between px-6 py-4 border-b border-border bg-gradient-to-r from-muted/50 to-card'>
        <div className='flex items-center gap-3'>
          <div className='w-9 h-9 rounded-xl border border-foreground/20 flex items-center justify-center shadow-md shadow-primary/10'>
            <Percent className='w-4 h-4' />
          </div>
          <div>
            <h3 className='text-[15px] font-semibold text-foreground'>
              Expense Ratio History
            </h3>
            <p className='text-xs text-muted-foreground mt-0.5'>
              Time-based overhead ratios applied on top of direct labor. Falls
              back to the single Expense Ratio above when empty.
            </p>
          </div>
        </div>
        {canMutate && (
          <Button
            size='sm'
            className='h-8 bg-primary hover:bg-primary/90 text-primary-foreground gap-1.5'
            onClick={() => setIsAddOpen(true)}
          >
            <Plus className='w-4 h-4' /> Add Period
          </Button>
        )}
      </div>

      <div className='flex-1 flex flex-col px-6 py-5 overflow-hidden'>
        {isExpired && (
          <div className='shrink-0 mb-6 p-4 rounded-2xl border border-amber-500/20 bg-amber-500/5 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-2 duration-500'>
            <div className='w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center text-amber-600 shrink-0'>
              <AlertTriangle className='w-5 h-5' />
            </div>
            <div className='flex-1'>
              <h4 className='text-sm font-bold text-amber-900 dark:text-amber-500'>
                Expense Ratio Expired
              </h4>
              <p className='text-xs text-amber-700/80 mt-0.5 leading-relaxed'>
                No active period covers today's date. The system is currently
                falling back to the standard ratio:{' '}
                <span className='font-bold'>{defaultRatio}%</span>. Add a new
                period to ensure accurate financial reporting.
              </p>
            </div>
            {canMutate && (
              <Button
                size='sm'
                className='h-8 bg-amber-500 hover:bg-amber-600 text-white rounded-lg shadow-sm active:scale-95 transition-all px-4'
                onClick={() => setIsAddOpen(true)}
              >
                Fix Now
              </Button>
            )}
          </div>
        )}

        {isLoading ? (
          <div className='space-y-2'>
            {[1, 2, 3].map(i => (
              <Skeleton key={i} className='h-10 w-full rounded-md' />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <p className='text-sm text-muted-foreground text-center py-6'>
            No expense ratio history yet — using the singleton Expense Ratio.
          </p>
        ) : (
          <div className='flex-1 overflow-auto border border-border rounded-xl'>
            <table className='min-w-full divide-y divide-border relative'>
              <thead className='bg-muted/60 sticky top-0 z-10 shadow-sm'>
                <tr>
                  <th className='px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider bg-muted/60'>
                    Expense Ratio
                  </th>
                  <th className='px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider bg-muted/60'>
                    Status
                  </th>
                  <th className='px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider bg-muted/60'>
                    Start Date
                  </th>
                  <th className='px-4 py-2 text-left text-xs font-semibold uppercase tracking-wider bg-muted/60'>
                    End Date
                  </th>
                  {canMutate && (
                    <th className='px-4 py-2 text-right text-xs font-semibold uppercase tracking-wider bg-muted/60'>
                      Actions
                    </th>
                  )}
                </tr>
              </thead>
              <tbody className='divide-y divide-border'>
                {rows.map(item => {
                  if (!item.start_date) return null
                  const start = dayjs(item.start_date).startOf('day')
                  const end = item.end_date
                    ? dayjs(item.end_date).startOf('day')
                    : null
                  const isActive =
                    (start.isBefore(today) || start.isSame(today)) &&
                    (!end || today.isBefore(end) || today.isSame(end))
                  const isUpcoming = start.isAfter(today)

                  return (
                    <tr key={item.id} className='hover:bg-muted/40'>
                      <td className='px-4 py-2 text-sm font-semibold text-foreground'>
                        {fractionToPercent(item.expense_ratio).toFixed(2)}%
                      </td>
                      <td className='px-4 py-2 text-sm'>
                        {isActive ? (
                          <span className='inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-emerald-500/10 text-emerald-600 border border-emerald-500/20'>
                            <span className='w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse' />
                            Active
                          </span>
                        ) : isUpcoming ? (
                          <span className='inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-blue-500/10 text-blue-600 border border-blue-500/20'>
                            Upcoming
                          </span>
                        ) : (
                          <span className='inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide bg-muted text-muted-foreground border border-border'>
                            Past
                          </span>
                        )}
                      </td>
                      <td className='px-4 py-2 text-sm'>
                        {formatDateDisplay(item.start_date)}
                      </td>
                      <td className='px-4 py-2 text-sm'>
                        {item.end_date
                          ? formatDateDisplay(item.end_date)
                          : 'Open'}
                      </td>
                      {canMutate && (
                        <td className='px-4 py-2 text-right'>
                          <div className='flex items-center justify-end gap-1'>
                            <Button
                              variant='ghost'
                              size='icon'
                              className='h-8 w-8'
                              onClick={() => {
                                setSelectedItem(item)
                                setIsEditOpen(true)
                              }}
                            >
                              <Edit className='h-4 w-4' />
                            </Button>
                            <Button
                              variant='ghost'
                              size='icon'
                              className='h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10'
                              onClick={() => {
                                setSelectedItem(item)
                                setIsDeleteOpen(true)
                              }}
                            >
                              <Trash className='h-4 w-4' />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ADD DIALOG */}
      <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle className='text-xl font-bold'>
              Add Expense Ratio Period
            </DialogTitle>
            <DialogDescription>
              Any currently-open period will be auto-closed one day before this
              start date.
            </DialogDescription>
          </DialogHeader>

          <Formik
            initialValues={{
              expense_ratio: '' as number | string,
              start_date: null as Date | null,
              end_date: null as Date | null,
            }}
            validationSchema={ExpenseRatioSchema}
            onSubmit={async (values, { resetForm }) => {
              try {
                await createExpenseRatio({
                  expense_ratio: percentToFraction(values.expense_ratio),
                  start_date: format(values.start_date!, 'yyyy-MM-dd'),
                  end_date: values.end_date
                    ? format(values.end_date, 'yyyy-MM-dd')
                    : null,
                }).unwrap()
                toast.success('Expense ratio added')
                resetForm()
                setIsAddOpen(false)
              } catch (err) {
                const error = err as { data?: { message?: string } }
                toast.error(error?.data?.message ?? 'Failed to add')
              }
            }}
          >
            {({ values, setFieldValue, setFieldTouched }) => (
              <Form className='space-y-4'>
                <div>
                  <Label className='text-sm font-medium mb-1.5 block'>
                    Expense Ratio (%){' '}
                    <span className='text-destructive'>*</span>
                  </Label>
                  <div className='relative'>
                    <Percent className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                    <Field
                      name='expense_ratio'
                      type='number'
                      min={0}
                      max={1000}
                      step='0.01'
                      as={Input}
                      placeholder='50'
                      className='pl-9'
                    />
                  </div>
                  <ErrorMessage
                    name='expense_ratio'
                    component='div'
                    className='text-destructive text-xs mt-1'
                  />
                </div>

                <div>
                  <Label className='text-sm font-medium mb-1.5 block'>
                    Start Date <span className='text-destructive'>*</span>
                  </Label>
                  <Popover open={addStartOpen} onOpenChange={setAddStartOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant='outline'
                        type='button'
                        className='w-full justify-start text-left font-normal h-10'
                      >
                        <CalendarIcon className='mr-2 h-4 w-4' />
                        {values.start_date
                          ? format(values.start_date, 'dd-MM-yyyy')
                          : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align='start' className='w-[350px]'>
                      <Calendar
                        mode='single'
                        captionLayout='dropdown'
                        numberOfMonths={1}
                        fromYear={2018}
                        toYear={2035}
                        selected={values.start_date ?? undefined}
                        onSelect={date => {
                          void setFieldValue('start_date', date)
                          if (values.end_date) {
                            void setFieldTouched('end_date', true, true)
                          }
                          setAddStartOpen(false)
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                  <ErrorMessage
                    name='start_date'
                    component='div'
                    className='text-destructive text-xs mt-1'
                  />
                </div>

                <div>
                  <Label className='text-sm font-medium mb-1.5 block'>
                    End Date (optional)
                  </Label>
                  <Popover open={addEndOpen} onOpenChange={setAddEndOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant='outline'
                        type='button'
                        className='w-full justify-start text-left font-normal h-10'
                      >
                        <CalendarIcon className='mr-2 h-4 w-4' />
                        {values.end_date
                          ? format(values.end_date, 'dd-MM-yyyy')
                          : 'Pick a date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align='start' className='w-[350px]'>
                      <Calendar
                        mode='single'
                        captionLayout='dropdown'
                        numberOfMonths={1}
                        fromYear={2018}
                        toYear={2035}
                        selected={values.end_date ?? undefined}
                        onSelect={date => {
                          void setFieldValue('end_date', date)
                          void setFieldTouched('end_date', true, true)
                          setAddEndOpen(false)
                        }}
                        disabled={date =>
                          values.start_date ? date < values.start_date : false
                        }
                      />
                    </PopoverContent>
                  </Popover>
                  <ErrorMessage
                    name='end_date'
                    component='div'
                    className='text-destructive text-xs mt-1'
                  />
                </div>

                <DialogFooter className='flex justify-end gap-2'>
                  <Button
                    type='button'
                    variant='outline'
                    onClick={() => setIsAddOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    disabled={isCreating}
                    className='gap-2 bg-primary text-primary-foreground hover:bg-primary/90'
                  >
                    {isCreating ? (
                      <>
                        <Loader2 className='w-4 h-4 animate-spin' /> Saving
                      </>
                    ) : (
                      <>
                        <Save className='w-4 h-4' /> Save Period
                      </>
                    )}
                  </Button>
                </DialogFooter>
              </Form>
            )}
          </Formik>
        </DialogContent>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle className='text-xl font-bold'>
              Update Expense Ratio Period
            </DialogTitle>
            <DialogDescription>
              Modify this row. Changes take effect immediately.
            </DialogDescription>
          </DialogHeader>

          {selectedItem && (
            <Formik
              initialValues={{
                expense_ratio: fractionToPercent(selectedItem.expense_ratio),
                start_date: selectedItem.start_date
                  ? new Date(selectedItem.start_date)
                  : null,
                end_date: selectedItem.end_date
                  ? new Date(selectedItem.end_date)
                  : null,
              }}
              validationSchema={ExpenseRatioSchema}
              onSubmit={async values => {
                try {
                  await updateExpenseRatio({
                    id: selectedItem.id,
                    data: {
                      expense_ratio: percentToFraction(values.expense_ratio),
                      start_date: format(values.start_date!, 'dd-MM-yyyy'),
                      end_date: values.end_date
                        ? format(values.end_date, 'dd-MM-yyyy')
                        : null,
                    },
                  }).unwrap()
                  toast.success('Expense ratio updated')
                  setIsEditOpen(false)
                  setSelectedItem(null)
                } catch (err) {
                  const error = err as { data?: { message?: string } }
                  toast.error(error?.data?.message ?? 'Update failed')
                }
              }}
            >
              {({ values, setFieldValue, setFieldTouched }) => (
                <Form className='space-y-4'>
                  <div>
                    <Label className='text-sm font-medium mb-1.5 block'>
                      Expense Ratio (%)
                    </Label>
                    <div className='relative'>
                      <Percent className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                      <Field
                        name='expense_ratio'
                        type='number'
                        min={0}
                        max={1000}
                        step='0.01'
                        as={Input}
                        className='pl-9'
                      />
                    </div>
                    <ErrorMessage
                      name='expense_ratio'
                      component='div'
                      className='text-destructive text-xs mt-1'
                    />
                  </div>

                  <div>
                    <Label className='text-sm font-medium mb-1.5 block'>
                      Start Date
                    </Label>
                    <Popover
                      open={editStartOpen}
                      onOpenChange={setEditStartOpen}
                    >
                      <PopoverTrigger asChild>
                        <Button
                          variant='outline'
                          type='button'
                          className='w-full justify-start text-left font-normal h-10'
                        >
                          <CalendarIcon className='mr-2 h-4 w-4' />
                          {values.start_date
                            ? format(values.start_date, 'dd-MM-yyyy')
                            : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align='start' className='w-[350px]'>
                        <Calendar
                          mode='single'
                          captionLayout='dropdown'
                          numberOfMonths={1}
                          fromYear={2018}
                          toYear={2035}
                          selected={values.start_date ?? undefined}
                          onSelect={date => {
                            void setFieldValue('start_date', date)
                            if (values.end_date) {
                              void setFieldTouched('end_date', true, true)
                            }
                            setEditStartOpen(false)
                          }}
                        />
                      </PopoverContent>
                    </Popover>
                    <ErrorMessage
                      name='start_date'
                      component='div'
                      className='text-destructive text-xs mt-1'
                    />
                  </div>

                  <div>
                    <Label className='text-sm font-medium mb-1.5 block'>
                      End Date (optional)
                    </Label>
                    <Popover open={editEndOpen} onOpenChange={setEditEndOpen}>
                      <PopoverTrigger asChild>
                        <Button
                          variant='outline'
                          type='button'
                          className='w-full justify-start text-left font-normal h-10'
                        >
                          <CalendarIcon className='mr-2 h-4 w-4' />
                          {values.end_date
                            ? format(values.end_date, 'dd-MM-yyyy')
                            : 'Pick a date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent align='start' className='w-[350px]'>
                        <Calendar
                          mode='single'
                          captionLayout='dropdown'
                          numberOfMonths={1}
                          fromYear={2018}
                          toYear={2035}
                          selected={values.end_date ?? undefined}
                          onSelect={date => {
                            void setFieldValue('end_date', date)
                            void setFieldTouched('end_date', true, true)
                            setEditEndOpen(false)
                          }}
                          disabled={date =>
                            values.start_date ? date < values.start_date : false
                          }
                        />
                      </PopoverContent>
                    </Popover>
                    <ErrorMessage
                      name='end_date'
                      component='div'
                      className='text-destructive text-xs mt-1'
                    />
                  </div>

                  <DialogFooter className='flex justify-end gap-2'>
                    <Button
                      type='button'
                      variant='outline'
                      onClick={() => {
                        setIsEditOpen(false)
                        setSelectedItem(null)
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type='submit'
                      disabled={isUpdating}
                      className='gap-2 bg-primary text-primary-foreground hover:bg-primary/90'
                    >
                      {isUpdating ? (
                        <>
                          <Loader2 className='w-4 h-4 animate-spin' /> Updating
                        </>
                      ) : (
                        <>
                          <Save className='w-4 h-4' /> Update
                        </>
                      )}
                    </Button>
                  </DialogFooter>
                </Form>
              )}
            </Formik>
          )}
        </DialogContent>
      </Dialog>

      {/* DELETE DIALOG */}
      <Dialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <DialogContent className='max-w-md'>
          <DialogHeader>
            <DialogTitle>Delete this period?</DialogTitle>
            <DialogDescription>
              This will remove the expense ratio entry. Time entries inside this
              window will fall back to the next available ratio.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className='mt-4 flex justify-end gap-2'>
            <Button
              variant='outline'
              onClick={() => {
                setIsDeleteOpen(false)
                setSelectedItem(null)
              }}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleConfirmDelete}
              disabled={isDeleting}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ExpenseRatioHistory
